/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import { CodedError } from '@karya/error-values';
import type { EmptyObject } from '@karya/misc-utils';
import type { KaryaDB } from './Setup';

/**
 * Details for {@link DatabaseInitializationError}.
 */
type DatabaseInitializationErrorDetails = {
  cause?: unknown;
};

/** Error indicating a failure to initialize the database. */
export class DatabaseInitializationError extends CodedError<
  'DatabaseInitializationError',
  DatabaseInitializationErrorDetails
> {
  constructor(cause?: unknown) {
    super('DatabaseInitializationError', 'Failed to initialize the database.', { cause });
  }
}

/** Error indicating that no active worker was found in the database. */
export class ActiveWorkerNotFoundError extends CodedError<'ActiveWorkerNotFoundError', EmptyObject> {
  constructor() {
    super('ActiveWorkerNotFoundError', 'No active worker found in the database.', {});
  }
}

/** Error indicating that no worker is currently logged in. */
export class WorkerNotLoggedInError extends CodedError<'WorkerNotLoggedInError', EmptyObject> {
  constructor() {
    super('WorkerNotLoggedInError', 'No worker is currently logged in.', {});
  }
}

/** Error indicating that a worker with the specified ID was not found in the database. */
export class WorkerNotFoundError extends CodedError<'WorkerNotFoundError', { workerId: string }> {
  constructor(workerId: string) {
    super('WorkerNotFoundError', `Worker with ID ${workerId} not found.`, { workerId });
  }
}

/** Details for {@link UpsertFailedError}. */
type UpsertFailedErrorDetails = { entity: keyof KaryaDB; cause?: unknown };

/** Error indicating a failure to upsert an entity in the database. */
export class UpsertFailedError extends CodedError<'UpsertFailedError', UpsertFailedErrorDetails> {
  constructor(entity: keyof KaryaDB, cause?: unknown) {
    super('UpsertFailedError', `Failed to upsert entity of type ${entity}.`, { entity, cause });
  }
}
